import React, { useMemo, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useApis } from '../../../api/useApiClient';
import { useNotification } from "../../../context/NotificationContext";

import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Button from "@mui/material/Button";
import Pagination from "@mui/material/Pagination";
import AddIcon from "@mui/icons-material/Add";

import SearchBox from "../SearchBox";
import LoadingTableBody from "../Skeletons/LoadingTableBody";
import EmptyTableBody from "../Skeletons/EmptyTableBody";


const ListView = ({
  title,
  apiPath, // e.g. "/item"
  filters = {},

  addButton = true, // By default, add button is enabled. Set this to false if add button is not needed.
  addButtonLabel = "Add new",
  addPath, // e.g. "/item/add"

  rowClickPath = null,
  onSelect = null,

  columnsConfig = {}, // A JSON object defining all possible columns for the list view. e.g. { name: { key, label, render }, type: { key, label, render } }
  columns = null, // An array of specific columns from the columnsConfig to show in the list view. If null, all columns from columnsConfig will be shown.

  searchBox = false,
  searchAttributes = [], // Array of search attribute configs for SearchBox
  // Each attribute: { name: string, label: string, type: 'text' | 'list' | 'date', options?: [{label, value}] }

  noResultsMessage = null
}) => {
  const { tenantApi } = useApis();
  const { showNotification } = useNotification();
  const navigate = useNavigate();

  const [pageNo, setPageNo] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);

  // Search filters from SearchBox
  const [searchFilters, setSearchFilters] = useState({});

  // Combine base filters with search filters
  const combinedFilters = useMemo(() => {
    const combined = {
      ...filters,
      ...searchFilters
    };

    // Remove null, undefined, or empty string filters
    Object.keys(combined).forEach(key => {
      if (
        combined[key] === null ||
        combined[key] === undefined ||
        combined[key] === ''
      ) {
        delete combined[key];
      }
    });

    return combined;
  }, [filters, searchFilters]);

  // Generate a stable key for combined filters
  const filtersKey = useMemo(
    () => JSON.stringify(combinedFilters),
    [combinedFilters]
  );

  // Handle filter changes from SearchBox
  const handleFilterChange = useCallback((newFilters) => {
    console.debug("ListView: Filter change from SearchBox:", newFilters);
    setSearchFilters(newFilters);
    setPageNo(1); // Reset to first page when filters change
  }, []);

  // Update page
  const handlePageChange = (event, value) => {
    setPageNo(value);
  };

  // Resolve columns to show
  const resolvedColumns = useMemo(() => {
    const allColumns = Object.values(columnsConfig);
    console.debug("ListView: allColumns:", columnsConfig);

    if (!columns || columns.length === 0) {
      return allColumns;
    }

    return columns
      .map(key => columnsConfig[key])
      .filter(Boolean);
  }, [columnsConfig, columns]);

  // optional safety check
  if (!resolvedColumns.length) {
    console.warn('ListView: no valid columns resolved');
  }

  async function getItems() {
    try {
      setLoading(true);

      // Extract only the filter params, excluding pagination
      const { pageNo: _, pageSize: __, ...filterParams } = combinedFilters;

      const res = await tenantApi(apiPath, {
        method: "GET",
        query: {
          pageNo,
          pageSize,
          ...filterParams
        },
      });

      setItems(res?.data);
      setTotalPages(res?.pagination?.totalPages || 1);
    } catch (e) {
      console.error(e);
      showNotification(
        e.message || "Failed to load data",
        "error"
      );
    } finally {
      setLoading(false);
    }
  }

  const handleSelect = (item) => {
    if (onSelect) onSelect(item);
    else if (rowClickPath) navigate(rowClickPath(item));
  };

  useEffect(() => {
    console.debug("ListView: Fetching items...", { pageNo, pageSize, filters: combinedFilters });
    getItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiPath, filtersKey, pageNo, pageSize]);

  return (
    <Grid container spacing={2}>
      {/* Header Row */}
      <Grid item xs={12} sm={6}>
        <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
          {title}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6} textAlign="right">
        {addButton && (
          <Button
            startIcon={<AddIcon />}
            onClick={() => navigate(addPath)}
          >
            {addButtonLabel}
          </Button>
        )}
      </Grid>

      {/* SearchBox */}
      {searchBox && searchAttributes.length > 0 && (
        <Grid item xs={12}>
          <SearchBox
            attributes={searchAttributes}
            debounceMs={400}
            onFilterChange={handleFilterChange}
          />
        </Grid>
      )}

      {/* Table */}
      <Grid item xs={12}>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                {resolvedColumns.map((col) => (
                  <TableCell key={col.key}>{col.label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && <LoadingTableBody colSpan={resolvedColumns.length} />}
              {!loading && items.length === 0 && (
                <EmptyTableBody colSpan={resolvedColumns.length} message={noResultsMessage} />
              )}
              {!loading && items.map((item) => (
                <TableRow
                  hover
                  key={item.id}
                  onClick={() => handleSelect(item)}
                  sx={{ textDecoration: "none", cursor: "pointer" }}
                >
                  {resolvedColumns.map((col) => (
                    <TableCell key={col.key}>
                      {col.render(item)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
          <Pagination
            count={totalPages}
            page={pageNo}
            onChange={handlePageChange}
          />
        </Box>
      </Grid>
    </Grid>
  );
};

export default ListView;
